import React, { createContext, useContext, useState, useEffect } from 'react';

const UIContext = createContext();

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};

export const UIProvider = ({ children }) => {
  const [theme, setTheme] = useState('dark');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Load theme from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('chess-app-theme');
    if (savedTheme) {
      setTheme(savedTheme);
    }
  }, []);

  // Save theme to localStorage
  useEffect(() => {
    localStorage.setItem('chess-app-theme', theme);
    
    // Apply theme to document
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const openSearch = () => {
    setSearchOpen(true);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
  };

  const addNotification = (notification) => {
    const id = Date.now().toString();
    const newNotification = {
      id,
      timestamp: new Date(),
      read: false,
      ...notification
    };
    
    setNotifications(prev => [newNotification, ...prev]);
    
    // Auto-remove notification after 5 seconds if it's a toast
    if (notification.type === 'toast') {
      setTimeout(() => {
        removeNotification(id);
      }, 5000);
    }
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  const markNotificationAsRead = (id) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === id ? { ...notif, read: true } : notif
      )
    );
  };

  const markAllNotificationsAsRead = () => {
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, read: true }))
    );
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  const showToast = (message, type = 'info') => {
    addNotification({
      type: 'toast',
      message,
      severity: type
    });
  };

  const showSuccess = (message) => {
    showToast(message, 'success');
  };

  const showError = (message) => {
    showToast(message, 'error');
  };

  const showWarning = (message) => {
    showToast(message, 'warning');
  };

  const showInfo = (message) => {
    showToast(message, 'info');
  };

  // Mock notifications for development
  useEffect(() => {
    const mockNotifications = [
      {
        id: '1',
        type: 'game_invite',
        message: 'TacticalTina challenged you to a 5+3 game',
        timestamp: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
        read: false,
        avatar: '⚔️'
      },
      {
        id: '2',
        type: 'friend_request',
        message: 'GrandmasterAlpha sent you a friend request',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
        read: false,
        avatar: '👑'
      },
      {
        id: '3',
        type: 'achievement',
        message: 'You earned the "Puzzle Master" achievement!',
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
        read: true,
        avatar: '🏆'
      }
    ];

    setNotifications(mockNotifications);
  }, []);

  const value = {
    // Theme
    theme,
    toggleTheme,
    
    // Sidebar
    sidebarOpen,
    toggleSidebar,
    closeSidebar,
    
    // Search
    searchOpen,
    searchQuery,
    setSearchQuery,
    openSearch,
    closeSearch,
    
    // Loading
    loading,
    setLoading,
    
    // Notifications
    notifications,
    addNotification,
    removeNotification,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    clearAllNotifications,
    
    // Toast helpers
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    
    // Computed values
    unreadNotificationsCount: notifications.filter(n => !n.read).length,
    hasUnreadNotifications: notifications.some(n => !n.read)
  };

  return (
    <UIContext.Provider value={value}>
      {children}
    </UIContext.Provider>
  );
};